'use client';

import { Avatar, cn, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import { Icon } from '@iconify/react';
import type { useSession } from '@/providers/enhanced-session';
import type { Key } from 'react';

const SettingsIcon = <Icon icon="lucide:settings" width={18} height={18} />;
const AdminIcon = <Icon icon="lucide:shield-check" width={18} height={18} />;
const HelpIcon = <Icon icon="lucide:life-buoy" width={18} height={18} />;
const WhatsNewIcon = <Icon icon="lucide:megaphone" width={18} height={18} />;
const SignOutIcon = <Icon icon="lucide:heart-crack" width={18} height={18} />;

interface NavFooterProps {
    isCollapsed: boolean;
    session: ReturnType<typeof useSession>['data'];
    onProfileMenuAction: (key: Key) => void;
}

/**
 * Footer chrome — every action is reused from the existing sidebar footer's
 * profile menu (`onProfileMenuAction`, already wired to Settings/Admin
 * Dashboard/Need Help?/Sign Out in `withSidebar.tsx`) rather than rebuilt.
 * The standalone Admin gear row and the personalization "sliders" row from
 * the reference screenshots are hidden for now (Admin Dashboard is still
 * reachable via the avatar dropdown below; personalization has no feature
 * behind it yet — PRD Phase 6, not built).
 */
export function NavFooter({ isCollapsed, session, onProfileMenuAction }: NavFooterProps) {
    const isInternal = !!session?.user?.isInternal;

    return (
        <div className="flex flex-col gap-1 px-1">
            <Dropdown placement="top-start">
                <DropdownTrigger>
                    <button
                        type="button"
                        className={cn(
                            'flex items-center gap-2 rounded-large py-1.5 hover:bg-[#F3F8FF]',
                            isCollapsed ? 'justify-center px-0' : 'px-2',
                        )}
                    >
                        <Avatar
                            isBordered
                            size="sm"
                            src={session?.user?.profileImage ?? ''}
                            name={session?.user?.name ?? ''}
                            getInitials={(name) =>
                                name
                                    ?.split(' ')
                                    .map((n) => n[0])
                                    .join('')
                            }
                            classNames={{ base: 'bg-[#192138] flex-shrink-0', name: 'text-white' }}
                        />
                        {!isCollapsed && (
                            <div className="flex min-w-0 flex-1 flex-col text-left">
                                <p className="truncate text-small font-medium text-default-600">
                                    {session?.user?.name}
                                </p>
                                <p className="truncate text-tiny text-default-400">
                                    {session?.user?.email}
                                </p>
                            </div>
                        )}
                    </button>
                </DropdownTrigger>
                <DropdownMenu
                    aria-label="Profile menu"
                    itemClasses={{
                        title: '!text-[13px]',
                        base: 'data-[hover=true]:!bg-[#F3F8FF]',
                    }}
                    onAction={onProfileMenuAction}
                >
                    <DropdownItem key="settings" startContent={SettingsIcon}>
                        Settings
                    </DropdownItem>
                    {isInternal ? (
                        <DropdownItem key="admin-dashboard" startContent={AdminIcon}>
                            Admin Dashboard
                        </DropdownItem>
                    ) : null}
                    {!session?.user?.isUnaffiliated ? (
                        <DropdownItem key="whats-new" startContent={WhatsNewIcon}>
                            What&apos;s new
                        </DropdownItem>
                    ) : null}
                    <DropdownItem key="need-help" startContent={HelpIcon}>
                        Need Help?
                    </DropdownItem>
                    <DropdownItem key="sign-out" startContent={SignOutIcon}>
                        Sign Out
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        </div>
    );
}

NavFooter.displayName = 'NavFooter';
